import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, RefreshCw, Search, Pencil, Trash2, ExternalLink,
  ChevronUp, ChevronDown, Package, Star, CheckCircle2, XCircle,
  Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { Product, Category } from '../../types';
import ProductFormModal from '../../components/admin/ProductFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import './Admin.css';
import './ProductManagement.css';
import '../../components/admin/MobileCardList.css';

const ProductManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getProducts();
      let productsList: Product[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) productsList = response.data;
        else if (response.data.results && Array.isArray(response.data.results)) productsList = response.data.results;
        else if (response.data.data && Array.isArray(response.data.data)) productsList = response.data.data;
      } else if (Array.isArray(response)) {
        productsList = response as any;
      }
      setProducts(productsList);
    } catch (error) {
      console.error('Failed to load products:', error);
      setToast({ show: true, message: 'Failed to load products', type: 'error' });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await adminAPI.getCategories();
      let categoriesList: Category[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) categoriesList = response.data;
        else if (response.data.results && Array.isArray(response.data.results)) categoriesList = response.data.results;
        else if (response.data.data && Array.isArray(response.data.data)) categoriesList = response.data.data;
      } else if (Array.isArray(response)) {
        categoriesList = response as any;
      }
      setCategories(categoriesList);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories([]);
    }
  };

  const handleEdit = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    try {
      await adminAPI.deleteProduct(selectedProduct.id);
      setToast({ show: true, message: 'Product deleted successfully', type: 'success' });
      loadProducts();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete product', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedProduct(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      let savedProduct;
      if (selectedProduct) {
        const response = await adminAPI.updateProduct(selectedProduct.id, formData);
        savedProduct = response.data;
        setToast({ show: true, message: 'Product updated successfully', type: 'success' });
      } else {
        const response = await adminAPI.createProduct(formData);
        savedProduct = response.data;
        setToast({ show: true, message: 'Product created successfully', type: 'success' });
      }
      loadProducts();
      return savedProduct;
    } catch (error: any) {
      console.error('Save error:', error);
      throw error;
    }
  };

  const handleAddNew = () => {
    setSelectedProduct(null);
    setIsModalOpen(true);
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortArrow = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ChevronUp size={10} className="pm-sort-arrow" />
      : <ChevronDown size={10} className="pm-sort-arrow" />;
  };

  // Filter + Sort
  const productsArray = Array.isArray(products) ? products : [];
  const filteredProducts = useMemo(() => {
    let list = productsArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p =>
        p.name_en?.toLowerCase().includes(q) ||
        p.name_bn?.toLowerCase().includes(q) ||
        (p as any).sku?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      list = list.filter(p => String((p as any).category) === categoryFilter);
    }
    if (statusFilter === 'active') list = list.filter(p => (p as any).is_active !== false);
    else if (statusFilter === 'inactive') list = list.filter(p => (p as any).is_active === false);
    else if (statusFilter === 'featured') list = list.filter(p => (p as any).is_featured);
    else if (statusFilter === 'out_of_stock') list = list.filter(p => (p as any).stock === 0);
    else if (statusFilter === 'low_stock') list = list.filter(p => (p as any).stock > 0 && (p as any).stock <= 10);

    // Sort
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return list;
  }, [productsArray, searchTerm, categoryFilter, statusFilter, sortField, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const paginated = filteredProducts.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1);
    if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  })();

  const stockChipClass = (stock: number) => {
    if (stock > 10) return 'pm-stock-ok';
    if (stock > 0) return 'pm-stock-low';
    return 'pm-stock-out';
  };
  const stockLabel = (stock: number) => {
    if (stock > 10) return stock;
    if (stock > 0) return stock;
    return 'Out';
  };

  const formatDate = (val: string) => {
    if (!val) return '-';
    return new Date(val).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="admin-page pm-page">

      {/* ── TOP BAR ── */}
      <div className="pm-topbar">
        <h1 className="pm-page-title">Product Management</h1>
        <div className="pm-topbar-right">
          <button className="pm-btn pm-btn-add" onClick={handleAddNew}>
            <Plus size={13} /> Add Product <span className="pm-btn-badge">{products.length}</span>
          </button>
          <button className="pm-btn pm-btn-refresh" onClick={loadProducts} disabled={loading}>
            {loading ? <span className="pm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="pm-filter-wrap">
        <div className="pm-filter-left">
          <select
            className="pm-filter-select"
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Categories</option>
            {categories.map((cat: any) => (
              <option key={cat.id} value={String(cat.id)}>{cat.name_en}</option>
            ))}
          </select>
          <select
            className="pm-filter-select"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            style={{ minWidth: 130 }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="featured">Featured ⭐</option>
            <option value="out_of_stock">Out of Stock</option>
            <option value="low_stock">Low Stock</option>
          </select>
          <span className="pm-count"><strong>{filteredProducts.length}</strong> products</span>
        </div>

        <div className="pm-filter-right">
          <div className="pm-search-wrap">
            <span className="pm-search-icon"><Search size={13} /></span>
            <input
              type="text"
              className="pm-search-input"
              placeholder="Search by name, SKU…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
      </div>

      {/* ── MOBILE CARD LIST ── */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading products…</div>}
          {!loading && filteredProducts.length === 0 && (
            <div className="mc-empty">No products found.</div>
          )}
          {!loading && filteredProducts.map((product: any) => (
            <div className="mobile-card" key={product.id}>
              <div className="mc-top">
                {product.image ? (
                  <img className="mc-thumb" src={product.image} alt={product.name_en} />
                ) : (
                  <div className="mc-thumb-placeholder"><Package size={22} /></div>
                )}
                <div className="mc-info">
                  <div className="mc-title">{product.name_bn || product.name_en}</div>
                  <div className="mc-subtitle">{product.category_name || 'Uncategorized'}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${product.is_active !== false ? 'active' : 'inactive'}`}>
                    {product.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <div className="mc-divider" />
              <div className="mc-meta">
                <div className="mc-meta-item">
                  <span className="mc-price">৳{Number(product.price || 0).toFixed(0)}</span>
                  {product.discount_price && (
                    <span className="mc-discount-price">৳{Number(product.discount_price).toFixed(0)}</span>
                  )}
                </div>
                {product.stock !== undefined && (
                  <div className="mc-meta-item">
                    <span className="mc-meta-label">Stock:</span>
                    <span className="mc-meta-value">{product.stock}</span>
                  </div>
                )}
              </div>
              <div className="mc-actions">
                <button className="mc-btn mc-btn-edit" onClick={() => handleEdit(product as Product)}>
                  <Pencil size={12} /> Edit
                </button>
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(product as Product)}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── DESKTOP TABLE ── */}
      {!isMobile && (
        <div className="pm-table-card">
          <div className="pm-table-scroll">
            <table className="pm-table">
              <colgroup>
                <col className="pm-col-img" />
                <col className="pm-col-name" />
                <col className="pm-col-sku" />
                <col className="pm-col-cat" />
                <col className="pm-col-price" />
                <col className="pm-col-stock" />
                <col className="pm-col-views" />
                <col className="pm-col-sales" />
                <col className="pm-col-status" />
                <col className="pm-col-featured" />
                <col className="pm-col-date" />
                <col className="pm-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="pm-th">Image</th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('name_en')}>
                    Product <SortArrow field="name_en" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('sku')}>
                    SKU <SortArrow field="sku" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('category_name')}>
                    Category <SortArrow field="category_name" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('price')}>
                    Price <SortArrow field="price" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('stock')}>
                    Stock <SortArrow field="stock" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('views_count')}>
                    Views <SortArrow field="views_count" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('sales_count')}>
                    Sales <SortArrow field="sales_count" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('is_active')}>
                    Status <SortArrow field="is_active" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('is_featured')}>
                    Featured <SortArrow field="is_featured" />
                  </th>
                  <th className="pm-th pm-th-sort" onClick={() => handleSort('created_at')}>
                    Created <SortArrow field="created_at" />
                  </th>
                  <th className="pm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={12} className="pm-loading">
                      <span className="pm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>
                      Loading products…
                    </td>
                  </tr>
                )}
                {!loading && paginated.length === 0 && (
                  <tr>
                    <td colSpan={12} className="pm-empty">No products found.</td>
                  </tr>
                )}
                {!loading && paginated.map((product: any) => {
                  const stock = product.stock ?? 0;
                  const isActive = product.is_active !== false;
                  const isFeatured = !!product.is_featured;
                  const hasDiscount = !!product.discount_price;

                  return (
                    <tr className="pm-row" key={product.id}>
                      {/* Image */}
                      <td className="pm-td">
                        {product.image ? (
                          <img src={product.image} alt={product.name_en} className="pm-img" />
                        ) : (
                          <div className="pm-img-placeholder"><Package size={18} /></div>
                        )}
                      </td>

                      {/* Name */}
                      <td className="pm-td">
                        {product.slug ? (
                          <a
                            href={`/products/${product.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="pm-product-link"
                          >
                            <span className="pm-product-name">{product.name_bn || product.name_en}</span>
                            <span className="pm-product-slug">{product.name_en}</span>
                          </a>
                        ) : (
                          <>
                            <span className="pm-product-name">{product.name_bn || product.name_en}</span>
                            <span className="pm-product-slug">{product.name_en}</span>
                          </>
                        )}
                      </td>

                      {/* SKU */}
                      <td className="pm-td">
                        <span className="pm-sku-text">{product.sku || '—'}</span>
                      </td>

                      {/* Category */}
                      <td className="pm-td">
                        <span className="pm-category-text">{product.category_name || '—'}</span>
                      </td>

                      {/* Price */}
                      <td className="pm-td">
                        {hasDiscount ? (
                          <>
                            <span className="pm-price-main">৳{Number(product.discount_price).toFixed(2)}</span>
                            <span className="pm-price-orig">৳{Number(product.price).toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="pm-price-main">৳{Number(product.price || 0).toFixed(2)}</span>
                        )}
                      </td>

                      {/* Stock */}
                      <td className="pm-td">
                        <span className={`pm-stock-chip ${stockChipClass(stock)}`}>
                          {stockLabel(stock)}
                        </span>
                      </td>

                      {/* Views */}
                      <td className="pm-td" style={{ color: '#64748b', fontWeight: 500 }}>
                        {product.views_count || 0}
                      </td>

                      {/* Sales */}
                      <td className="pm-td" style={{ color: '#1a56db', fontWeight: 600 }}>
                        {product.sales_count || 0}
                      </td>

                      {/* Active Status */}
                      <td className="pm-td">
                        <span className={`pm-status-chip ${isActive ? 'pm-status-active' : 'pm-status-inactive'}`}>
                          {isActive
                            ? <><CheckCircle2 size={9} /> Active</>
                            : <><XCircle size={9} /> Inactive</>
                          }
                        </span>
                      </td>

                      {/* Featured */}
                      <td className="pm-td">
                        {isFeatured ? (
                          <span className="pm-featured-chip pm-featured-yes"><Star size={9} /> Featured</span>
                        ) : (
                          <span className="pm-featured-no" title="Not Featured">—</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="pm-td">
                        <span className="pm-date-text">{formatDate(product.created_at)}</span>
                      </td>

                      {/* Actions */}
                      <td className="pm-td" style={{ padding: '6px 4px' }}>
                        <div className="pm-actions-row">
                          <button
                            className="pm-action-btn pm-action-edit"
                            onClick={() => handleEdit(product as Product)}
                            title="Edit Product"
                          >
                            <Pencil size={12} />
                          </button>
                          {product.slug && (
                            <button
                              className="pm-action-btn pm-action-view"
                              onClick={() => window.open(`/products/${product.slug}`, '_blank')}
                              title="View on site"
                            >
                              <ExternalLink size={12} />
                            </button>
                          )}
                          <button
                            className="pm-action-btn pm-action-del"
                            onClick={() => handleDeleteClick(product as Product)}
                            title="Delete Product"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── PAGINATION ── */}
          <div className="pm-pagination">
            <div className="pm-page-size-selector">
              <span>Show</span>
              <select
                className="pm-page-size-select"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              >
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span>per page</span>
            </div>

            <div className="pm-page-info">
              {filteredProducts.length === 0 ? '0 products' :
                `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filteredProducts.length)} of ${filteredProducts.length} products`}
            </div>

            <div className="pm-page-controls">
              <button
                className="pm-page-btn"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              >‹</button>
              <div className="pm-page-numbers">
                {pageNums.map((n, i) =>
                  n === '...'
                    ? <span key={`e${i}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                    : (
                      <button
                        key={n}
                        className={`pm-page-num${currentPage === n ? ' active' : ''}`}
                        onClick={() => setCurrentPage(n as number)}
                      >{n}</button>
                    )
                )}
              </div>
              <button
                className="pm-page-btn"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              >›</button>
            </div>
          </div>
        </div>
      )}

      <ProductFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedProduct(null); }}
        onSave={handleSave}
        product={selectedProduct}
        categories={categories}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => { setIsDeleteDialogOpen(false); setSelectedProduct(null); }}
        onConfirm={handleDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${selectedProduct?.name_en}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default ProductManagement;
